import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { linkStyles } from "@store/theme/common/mixins";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showBanner = "showBanner",
    showTitle = "showTitle",
}
type OwnProps = HasCategoryContext;

const mapStateToProps = (state: StoreApplicationState, props: OwnProps) => {
	const categoriesDataView = props.category?.id ? getCategoriesDataView(state, { maxDepth: 1, startCategoryId: props.category?.id }) : undefined;
    const brandId = props.category?.id.includes(':') ? props.category?.id.split(':')[1] : "";
    const brand = brandId ? getBrandStateById(state, brandId).value : undefined;

    return {
        categoriesDataView,
        brandId,
        brand,
    };
};

const mapDispatchToProps = {
    loadCategories,
    loadBrand,
};

type Props = OwnProps & WidgetProps & 
	ReturnType<typeof mapStateToProps> &
	ResolveThunks<typeof mapDispatchToProps> &
	HasHistory;

export interface CategoryMainImageStyles {
    categoryTitleWrapper?: InjectableCss;
    brandLink?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    buttonWrapper?: InjectableCss;
    shopAllButton?: ButtonPresentationProps;
    titleShopAllLink?: LinkPresentationProps;
}

export const categoryMainImageStyles: CategoryMainImageStyles = {
    categoryTitleWrapper: {
        css: css`
            display: flex;
            position: relative;
            flex-direction: row;
            align-items: baseline;
        `,
    },
    title: {
		variant: "h1",
		css: css`
            display: flex;
            flex-direction: column;
            padding-bottom: 1.5em;
            margin-bottom: 0;
		`,
	},
    brandLink: {
		typographyProps: {
            variant: "h3",
        },
	},
    titleShopAllLink: {
        css: css`
            ${linkStyles("text.link", "text", true)}
            font-weight: ${TYPOGRAPHY.weight.bold};
            margin-left: 1em;
        `,
    },
	centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["20px", "24px", "36px", "48px"], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    buttonWrapper: {
        css: css`
			position: absolute;
			text-align: center;
			width: 100%;

			${props =>
				breakpointValuesMediaQueries(
					props.theme,
					[{ values: ["22px", "26px", "40px", "44px"], properties: ["bottom"] }],
					mediaOptions.min,
				)}
        `,
    },
    shopAllButton: {
		variant: "primary",
        css: css`
            box-shadow: ${getProp("theme.shadows.3")};
        `,
    },
};

const styles = categoryMainImageStyles;

const CategoryMainImage = ({ 
    category,
    history,
    categoriesDataView,
    loadCategories,
    brandId,
    loadBrand,
    brand,
    fields: { showBanner, showTitle },
}: Props) => {
    React.useEffect(() => {
        if (category && !categoriesDataView?.isLoading && !categoriesDataView?.value) {
            loadCategories({ maxDepth: 1, startCategoryId: category?.id });
            
            if (brandId) {
                loadBrand({brandId});
            }
        }
    }, [category]);

    if (!category) {
        return null;
    }

	if (!categoriesDataView || categoriesDataView?.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

	if (!categoriesDataView?.value) {
        return null;
    }

    const categories = categoriesDataView.value;
    const shopAllCategory = categories.filter(x => x.properties.isShopAll === "true");
    const shopAllCategoryPath = shopAllCategory.map(x => x.path)[0];
    const showShopAllLinkNextToTitle = shopAllCategoryPath && showTitle && !showBanner;

    const onShopAllClick = () => {
        if (!shopAllCategoryPath) {
            return;
        }
        history.push(shopAllCategoryPath);
    };

    return (
        <>
            {showTitle && (
                <StyledWrapper {...styles.categoryTitleWrapper}>
                    <Typography {...styles.title}>
                        {brand && (
                            <Link href={brand.detailPagePath} {...styles.brandLink}>
                                {brand.name}
                            </Link>
                        )}
                        {category.shortDescription}
                    </Typography>
                    {(showShopAllLinkNextToTitle && !brand) && (
                        <Link href={shopAllCategoryPath} {...styles.titleShopAllLink}>
                            {translate("Shop All")}
                        </Link>
                    )}
                </StyledWrapper>
            )}
            {showBanner && (
                <StyledWrapper {...styles.wrapper}>
                    <LazyImage
                        src={category.largeImagePath || COMMON.defaultLargeCategoryImagePath}
                        altText={category.imageAltText}
                        {...styles.image}
                    />
                    {(shopAllCategoryPath && !brand) && (
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Button 
                                {...styles.shopAllButton}
                                onClick={onShopAllClick}
                            >
                                {translate("Shop All {0}", `${category.shortDescription}`)}
                            </Button>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(connect(mapStateToProps, mapDispatchToProps)(withHistory(CategoryMainImage))),
    definition: {
        group: "Categories",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.showBanner,
                displayName: "Show Banner",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
